import { createContext, useContext, useEffect, useState } from "react";
import { setUser as sentrySetUser, clearUser as sentryClearUser } from "@/lib/sentry";
import { SCOPE_GLOBAL_ROLES } from "@/lib/roles";

export type UserRole =
  | "superadmin"
  | "econome"
  | "adjoint"
  | "cabinet"
  | "achats"
  | "procure"
  | "paroisses"
  | "projets"
  | "communication"
  | "seminaires"
  | "sysadmin"
  // Auditeur diocésain : lecture seule (cf. mapRole).
  | "auditeur"
  // Rôles diocésains SEDEC (econome_diocesain, sedec_admin).
  | "sedec"
  // Caissier de l'Économat (06/10/2026) : Caisse des versements et
  // Reversements des paroisses seulement (liste blanche de chemins, cf.
  // ROLE_PATHS), plus son espace personnel.
  | "caissier"
  // Suivi des dons en lecture seule (rôle auth-service « suivi_dons »,
  // décision du PO du 20/10/2026) : la seule page « Suivi des dons »,
  // quand le compte n'a aucun autre rôle de l'Économat.
  | "suivi_dons"
  // Aucun rôle reconnu : écran « Accès non configuré », aucune section.
  | "non_configure";

export interface AuthUser {
  username: string;
  role: UserRole;
  displayName: string;
  direction: string;
  initials: string;
  color: string;
  redirect: string;
  /**
   * Rôles bruts renvoyés par auth-service (minuscule). Sert aux écrans qui
   * doivent connaître le rôle réel (ex. création d'un compte : seuls admin et
   * super_admin peuvent attribuer un rôle privilégié). Absent des sessions
   * ouvertes avant le 05/10/2026 dont le jeton est illisible.
   */
  backendRoles?: string[];
  /** Profil en lecture seule (auditeur) : boutons d'écriture masqués. */
  readOnly?: boolean;
  /**
   * Rattachement porté par le jeton (claims parishId, ecoleId,
   * school_permissions). Un économe rattaché à une paroisse ou à une école
   * n'est pas l'Économat diocésain : auth-service et donation-service le
   * cantonnent à sa paroisse (ou ne lui ouvrent rien). Absent : aucun claim
   * lu (session ancienne ou jeton illisible), traité comme sans rattachement.
   */
  rattachement?: Rattachement;
}

export interface Rattachement {
  /** Paroisse du jeton (claim parishId), ou null. */
  paroisse: string | null;
  /** Vrai si le jeton porte un établissement scolaire (ecoleId ou school_permissions). */
  ecole: boolean;
}

// Exporté pour la garde de route client (client/src/components/layout/
// section-guard.tsx) : seules les sections de cet univers sont opposables à
// un utilisateur — une route mappée sur une section hors univers (ex.
// "contenu-diocese", absente de ROLE_SECTIONS) n'est pas gardée.
// Sections = celles du rail du prototype DA (organigramme de l'Économat) :
// general, cabinet, finances, perequation, procure, achats, projets,
// seminaires, paroisses, sedec, communication, admin. Chaque direction
// retrouve ainsi SA section (avant : 16 sections techniques sans rapport —
// un utilisateur « procure » ne voyait aucune section « Procure »).
export const ALL_SECTIONS = [
  "essentiel", "general", "cabinet", "finances", "perequation", "procure",
  "achats", "projets", "seminaires", "paroisses", "sedec",
  "communication", "admin",
];

const ROLE_SECTIONS: Record<UserRole, string[]> = {
  superadmin:    ALL_SECTIONS,
  // econome : rail complet, sauf l'espace Paiements, réservé aux
  // administrateurs globaux (GLOBAL_ADMIN_PATHS, 19/10/2026).
  econome:       ALL_SECTIONS,
  adjoint:       ["essentiel", "general", "cabinet", "finances", "perequation", "paroisses", "seminaires"],
  cabinet:       ["cabinet", "general", "communication"],
  achats:        ["achats", "procure", "cabinet"],
  procure:       ["procure", "achats", "finances", "cabinet"],
  paroisses:     ["paroisses", "perequation", "cabinet"],
  projets:       ["projets", "finances", "cabinet"],
  communication: ["communication", "cabinet"],
  seminaires:    ["seminaires", "sedec", "cabinet"],
  sysadmin:      ["admin", "cabinet"],
  // Le backend n'accepte auditeur_diocesain, econome_diocesain et sedec_admin
  // que sur school-service (agrégats /api/sedec/*, en lecture) : finance-,
  // parish- et diocese-service les refusent. On n'ouvre donc que SEDEC (et
  // l'espace personnel du Cabinet), jamais le rail complet.
  auditeur:      ["sedec", "cabinet"],
  sedec:         ["sedec", "cabinet"],
  // Le caissier n'a « finances » que pour deux pages (ROLE_PATHS) et
  // « cabinet » pour son espace personnel (/mon-compte).
  caissier:      ["finances", "cabinet"],
  // Suivi des dons seul : la section Direction générale, réduite à la page
  // « Suivi des dons » par ROLE_PATHS.
  suivi_dons:    ["general"],
  non_configure: [],
};

/** Page « Suivi des dons » de l'Économat (rail Direction générale). */
export const SUIVI_DONS_PATH = "/suivi-dons";
/** Écran de paramétrage « Accès au suivi des dons ». */
export const ACCES_SUIVI_DONS_PATH = "/suivi-dons/acces";

/**
 * Rôles auth-service que donation-service admet sur GET
 * /api/donations/admin/suivi (SuiviDonsResource) : administrateurs globaux,
 * Économat diocésain (econome, adjoint) et le rôle de lecture seule
 * suivi_dons. edaci_admin, qui ouvre pourtant le rail complet, en est exclu
 * par le serveur : la page ne lui est pas proposée (sinon un 403 sur un
 * chemin « /admin », que CrowdSec surveille).
 */
export const ROLES_SUIVI_DONS = ["admin", "super_admin", "econome", "adjoint", "suivi_dons"] as const;

/**
 * Rôles qui lisent le journal des dons (GET /api/donations/admin/list), donc
 * le nom des donateurs qui ne sont pas anonymes. adjoint et suivi_dons n'y
 * ont pas accès.
 */
export const ROLES_JOURNAL_DONS = ["admin", "edaci_admin", "super_admin", "econome", "vicariat_admin"] as const;

function rolesBas(user: Pick<AuthUser, "backendRoles">): string[] | null {
  return user.backendRoles ? user.backendRoles.map((r) => r.toLowerCase()) : null;
}

/** Vrai si l'utilisateur peut ouvrir la page « Suivi des dons ». */
export function peutLireSuiviDons(user: Pick<AuthUser, "role" | "backendRoles">): boolean {
  const roles = rolesBas(user);
  if (!roles) return ["superadmin", "econome", "adjoint", "suivi_dons"].includes(user.role);
  return (ROLES_SUIVI_DONS as readonly string[]).some((r) => roles.includes(r));
}

/** Vrai si l'utilisateur lit le journal des dons (noms des donateurs). */
export function peutLireJournalDons(user: Pick<AuthUser, "role" | "backendRoles">): boolean {
  const roles = rolesBas(user);
  if (!roles) return user.role === "superadmin" || user.role === "econome";
  return (ROLES_JOURNAL_DONS as readonly string[]).some((r) => roles.includes(r));
}

/**
 * Vrai si le suivi porte sur tout le diocèse : aucun rattachement à une
 * paroisse ni à une école dans le jeton. La santé des paiements n'est servie
 * qu'à ce périmètre (403 sinon).
 */
export function suiviDuDiocese(user: Pick<AuthUser, "rattachement">): boolean {
  return !user.rattachement || (!user.rattachement.paroisse && !user.rattachement.ecole);
}

/**
 * Économat diocésain ou administrateur global : seuls à donner ou retirer
 * l'accès au suivi des dons (auth-service, AccesSuiviDonsResource : admin,
 * super_admin, ou econome sans paroisse ni école dans son jeton).
 */
export function peutGererAccesSuiviDons(
  user: Pick<AuthUser, "role" | "backendRoles" | "rattachement">,
): boolean {
  if (isGlobalAdmin(user)) return true;
  const roles = rolesBas(user);
  if (!roles || !roles.includes("econome")) return false;
  return suiviDuDiocese(user);
}

/**
 * Liste blanche de CHEMINS pour les rôles dont la section est trop large.
 * Un rôle absent de cette table garde toutes les pages de ses sections ;
 * un rôle présent n'a que les chemins listés (égalité ou sous-chemin), le
 * rail, la palette Ctrl+K et SectionGuard appliquent la même règle.
 *
 * caissier (06/10/2026) : finance-service lui ouvre l'encaissement à la
 * caisse, le journal, les reçus et la lecture des versements ; rien d'autre
 * de l'Économat ne lui est accessible côté serveur, le client suit.
 */
const ROLE_PATHS: Partial<Record<UserRole, readonly string[]>> = {
  caissier: ["/caisse-versements", "/reversements", "/mon-compte"],
  // Un compte qui n'a que suivi_dons ouvre l'Économat sur cette seule page.
  suivi_dons: [SUIVI_DONS_PATH],
};

/** Vrai si le rôle a le droit d'ouvrir ce chemin (liste blanche ou libre). */
export function pathAllowedForRole(role: UserRole, path: string): boolean {
  const allowed = ROLE_PATHS[role];
  if (!allowed) return true;
  return allowed.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Chemins réservés aux administrateurs globaux (rôles auth-service admin et
 * super_admin), quel que soit le profil : tout l'espace Paiements « CATHO+
 * Pay » (tableau Paiements, Prestataires, Opérateurs, Causes, Liste des dons,
 * Marchands, Notifications PSP, Traçabilité, Routage).
 *
 * Diagnostic du 19/10/2026 (4.2.3) : « econome » est aussi le rôle de
 * l'économe d'une paroisse ou d'une école, et la passerelle réserve
 * /api/v1/admin/** aux administrateurs. Une page que le serveur refuse
 * enchaîne des 403 sur des chemins « /admin », que CrowdSec sanctionne par un
 * bannissement de l'adresse pendant 4 h : la page n'est donc ni dans le rail,
 * ni dans la palette, et un lien direct rend la page 403.
 */
const GLOBAL_ADMIN_PATHS: readonly string[] = ["/admin/payment"];

/**
 * Administrateur global (admin, super_admin). Session ancienne dont le jeton
 * est illisible (pas de rôles bruts) : on s'en tient au profil.
 */
export function isGlobalAdmin(user: Pick<AuthUser, "role" | "backendRoles">): boolean {
  if (!user.backendRoles) return user.role === "superadmin";
  const lower = user.backendRoles.map((r) => r.toLowerCase());
  return (SCOPE_GLOBAL_ROLES as readonly string[]).some((r) => lower.includes(r));
}

/** Chemin ouvert à l'utilisateur : règles du profil, puis chemins réservés. */
export function pathAllowedForUser(
  user: Pick<AuthUser, "role" | "backendRoles" | "rattachement">,
  path: string,
): boolean {
  // Suivi des dons : ouvert à qui porte un rôle admis par donation-service,
  // quel que soit son profil (l'accès suivi_dons s'AJOUTE aux autres rôles :
  // un caissier ou le service communication peut l'avoir). Même lecture sans
  // casse ni barre finale que wouter.
  const chemin = path.toLowerCase().replace(/\/+$/, "") || "/";
  if (chemin === SUIVI_DONS_PATH) return peutLireSuiviDons(user);
  if (chemin === ACCES_SUIVI_DONS_PATH) return peutGererAccesSuiviDons(user);
  if (!pathAllowedForRole(user.role, path)) return false;
  // wouter compare les routes sans tenir compte de la casse : « /Admin/Payment »
  // ouvre la même page, il doit donc être refusé de la même façon.
  const bas = path.toLowerCase();
  if (GLOBAL_ADMIN_PATHS.some((p) => bas === p || bas.startsWith(`${p}/`))) return isGlobalAdmin(user);
  return true;
}

/** Page d'accueil propre à certains rôles (prime sur HOME_BY_SECTION). */
const HOME_BY_ROLE: Partial<Record<UserRole, string>> = {
  // Flux financiers (02/11/2026) : l'Économat diocésain arrive sur la chaîne
  // de l'argent, de la paroisse à l'Économat.
  superadmin: "/flux",
  econome: "/flux",
  adjoint: "/flux",
  caissier: "/caisse-versements",
  suivi_dons: SUIVI_DONS_PATH,
};

// Page d'atterrissage par section (première section autorisée du rôle) :
// sans cela, tous les rôles étaient redirigés vers /dashboard (section gov)
// et les rôles scopés atterrissaient sur un 403 malgré une session valide.
// ⚠️ Chaque cible DOIT être une route exacte déclarée dans App.tsx : wouter ne
// matche pas les préfixes, une cible sans route atterrit sur NotFound (sans
// coquille ni page 403) — c'est ce qui cassait le login sysadmin (/admin).
const HOME_BY_SECTION: Record<string, string> = {
  general: "/dashboard",
  cabinet: "/mon-compte",
  finances: "/tresorerie",
  perequation: "/perequation",
  procure: "/fournisseurs",
  achats: "/fournisseurs",
  projets: "/projets",
  seminaires: "/formation-diocesaine",
  paroisses: "/paroisses",
  sedec: "/sedec/pilotage/effectifs",
  communication: "/communication",
  admin: "/admin/utilisateurs",
};

/** Écran rendu à tout compte dont aucun rôle n'est reconnu par l'Économat. */
export const ACCES_NON_CONFIGURE_PATH = "/acces-non-configure";

export function homeForRole(role: UserRole): string {
  if (role === "non_configure") return ACCES_NON_CONFIGURE_PATH;
  const propre = HOME_BY_ROLE[role];
  if (propre) return propre;
  for (const section of ROLE_SECTIONS[role] ?? []) {
    const home = HOME_BY_SECTION[section];
    if (home) return home;
  }
  return "/mon-compte";
}

/**
 * Home de l'utilisateur courant (rail « C+ », redirection post-login,
 * RouteGuard). Ne JAMAIS coder « /dashboard » en dur : cette route est mappée
 * sur la section « general », absente de 7 rôles sur 11 (cabinet hors general
 * exclu, achats, procure, paroisses, projets, communication, seminaires,
 * sysadmin) → le rôle atterrissait sur un 403 juste après un login valide.
 * Sans utilisateur (rendu avant hydratation), on retombe sur /dashboard.
 */
export function homeForUser(user: AuthUser | null): string {
  if (!user) return "/dashboard";
  return user.redirect || homeForRole(user.role);
}

const TOKEN_KEY = "economat_access_token";
const REFRESH_KEY = "economat_refresh_token";
const EXPIRES_KEY = "economat_token_expires";
const USER_KEY = "economat_user";
/** Préférence « Se souvenir de moi » (case du login). "0" = session seulement. */
const REMEMBER_KEY = "economat_remember";

/**
 * Support de persistance de la session, piloté par la case « Se souvenir de
 * moi » du login (elle était purement décorative avant) :
 *  - cochée (défaut, comportement historique) → localStorage : la session
 *    survit à la fermeture du navigateur ;
 *  - décochée → sessionStorage : les jetons disparaissent à la fermeture de
 *    l'onglet/navigateur (poste partagé).
 * Les LECTURES regardent les deux supports (localStorage d'abord) : une
 * session déjà ouverte reste valide quel que soit le choix.
 */
function isRemembered(): boolean {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(REMEMBER_KEY) !== "0";
}

function sessionStore(): Storage {
  return isRemembered() ? localStorage : sessionStorage;
}

function readPersisted(key: string): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

/** Mémorise le choix de la case « Se souvenir de moi » (appelé avant le login). */
export function setRememberMe(remember: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
}

/** État courant de la préférence — pré-coche la case au montage du login. */
export function getRememberMe(): boolean {
  return isRemembered();
}

let accessToken: string | null = readPersisted(TOKEN_KEY);

export function getAccessToken(): string | null {
  return accessToken;
}

function persistTokens(access: string, refresh: string | null, expiresIn: number) {
  accessToken = access;
  const store = sessionStore();
  const other = store === localStorage ? sessionStorage : localStorage;
  store.setItem(TOKEN_KEY, access);
  if (refresh) store.setItem(REFRESH_KEY, refresh);
  store.setItem(EXPIRES_KEY, String(Date.now() + expiresIn * 1000));
  // Jamais deux copies : on purge le support non retenu.
  other.removeItem(TOKEN_KEY);
  other.removeItem(REFRESH_KEY);
  other.removeItem(EXPIRES_KEY);
  other.removeItem(USER_KEY);
}

function clearTokens() {
  accessToken = null;
  for (const store of [localStorage, sessionStorage]) {
    store.removeItem(TOKEN_KEY);
    store.removeItem(REFRESH_KEY);
    store.removeItem(EXPIRES_KEY);
    store.removeItem(USER_KEY);
  }
}

// Rôles économat scopés (section-level RBAC via ROLE_SECTIONS). Chaque nom
// correspond 1:1 à un rôle auth-service (migration V10__economat_roles.sql).
const ECONOMAT_SCOPED_ROLES: UserRole[] = [
  "adjoint", "cabinet", "achats", "procure", "paroisses",
  "projets", "communication", "seminaires", "sysadmin",
];

/**
 * Rôles auth-service → profil Économat. Ordre = du plus large au plus étroit.
 *
 * Aucun repli : un rôle non reconnu (secretaire, lecteur, parent, user…)
 * donne « non_configure » (écran « Accès non configuré »). Avant le
 * 05/10/2026 le repli était « econome » : tout compte inconnu recevait le
 * rail complet avec ses boutons d'écriture (SEC-6).
 */
export function mapRole(roles: string[]): UserRole {
  const lower = roles.map((r) => String(r).toLowerCase());
  if (lower.some((r) => ["super_admin", "superadmin", "admin", "edaci_admin"].includes(r))) return "superadmin";
  if (lower.includes("econome")) return "econome";
  // Rôle économat scopé explicite (adjoint, cabinet, achats, …)
  const scoped = ECONOMAT_SCOPED_ROLES.find((r) => lower.includes(r));
  if (scoped) return scoped;
  if (lower.includes("cure") || lower.includes("priest")) return "paroisses";
  // Caissier de l'Économat : deux pages, en écriture (prime sur les rôles
  // SEDEC en lecture).
  if (lower.includes("caissier")) return "caissier";
  if (lower.includes("econome_diocesain") || lower.includes("sedec_admin")) return "sedec";
  if (lower.includes("auditeur_diocesain")) return "auditeur";
  // Accès au suivi des dons seul (lecture seule) : une page, rien d'autre.
  if (lower.includes("suivi_dons")) return "suivi_dons";
  return "non_configure";
}

export function isReadOnlyRole(role: UserRole): boolean {
  return role === "auditeur";
}

/**
 * Rôles portés par le jeton (claim « groups » émis par auth-service).
 * Sert à recalculer le profil d'une session déjà ouverte : le profil
 * persisté a pu être calculé par une ancienne version de mapRole (repli
 * « econome »). Jeton illisible → null (on garde le profil persisté).
 */
export function rolesFromAccessToken(token: string | null): string[] | null {
  if (!token) return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")));
    const groups = json.groups ?? json.roles;
    return Array.isArray(groups) ? groups.map(String) : null;
  } catch {
    return null;
  }
}

function chargeUtileDuJeton(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")));
    return json && typeof json === "object" ? (json as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function claimRenseigne(valeur: unknown): boolean {
  if (valeur == null) return false;
  if (typeof valeur === "string") return valeur.trim() !== "" && valeur.trim() !== "null";
  if (Array.isArray(valeur)) return valeur.length > 0;
  if (typeof valeur === "object") return Object.keys(valeur as object).length > 0;
  return true;
}

/**
 * Rattachement lu dans le jeton (claims parishId, ecoleId,
 * school_permissions), même lecture qu'auth-service et donation-service.
 * Jeton illisible : null.
 */
export function rattachementDuJeton(token: string | null): Rattachement | null {
  const charge = chargeUtileDuJeton(token);
  if (!charge) return null;
  const paroisse = claimRenseigne(charge.parishId) ? String(charge.parishId) : null;
  const ecole = claimRenseigne(charge.ecoleId) || claimRenseigne(charge.school_permissions);
  return { paroisse, ecole };
}

/** Réaligne un profil persisté sur les rôles du jeton courant. */
export function withRolesFromToken(user: AuthUser, token: string | null): AuthUser {
  const roles = rolesFromAccessToken(token);
  if (!roles) return user;
  const role = mapRole(roles);
  return {
    ...user,
    role,
    backendRoles: roles.map((r) => r.toLowerCase()),
    readOnly: isReadOnlyRole(role),
    redirect: homeForRole(role),
    rattachement: rattachementDuJeton(token) ?? undefined,
  };
}

export interface LoginCredentials {
  username?: string;
  email?: string;
  password: string;
}

/**
 * Le compte a la double authentification (activée dans admin-eda) : la
 * connexion attend le code de l'application d'authentification (plan
 * admin-eda du 28/09/2026, 7h).
 */
export class DoubleAuthRequise extends Error {
  constructor(public mfaSession: string) {
    super("Code de double authentification requis");
  }
}

export async function loginWithCredentials(creds: LoginCredentials): Promise<AuthUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(creds),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Erreur d'authentification (${res.status})`);
  }
  const data = await res.json();
  if (data.mfaRequired) throw new DoubleAuthRequise(data.mfaSession);
  return ouvrirSession(data, creds);
}

/** Seconde étape : le code à 6 chiffres (ou un code de secours). */
export async function loginWithMfaCode(mfaSession: string, code: string, creds: LoginCredentials): Promise<AuthUser> {
  const res = await fetch("/api/auth/login/mfa", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mfaSession, code: code.replace(/\s/g, "") }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      res.status === 423
        ? "Trop de codes faux : réessayez dans quelques minutes."
        : res.status === 401 && data.message === "Code invalide"
          ? "Code invalide."
          : data.message || `Erreur d'authentification (${res.status})`,
    );
  }
  return ouvrirSession(data, creds);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ouvrirSession(data: any, creds: LoginCredentials): AuthUser {
  persistTokens(data.accessToken, data.refreshToken ?? null, data.expiresIn ?? 3600);

  const role = mapRole(data.roles || []);
  const displayName = `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim() || data.email || data.username || "Utilisateur";
  const initials = displayName
    .split(/\s+/)
    .map((s: string) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const user: AuthUser = {
    username: data.email || data.username || creds.email || creds.username || "",
    role,
    displayName,
    direction: data.direction || "",
    initials,
    color: "bg-slate-100",
    redirect: homeForRole(role),
    backendRoles: (data.roles || []).map((r: unknown) => String(r).toLowerCase()),
    readOnly: isReadOnlyRole(role),
    rattachement: rattachementDuJeton(data.accessToken ?? null) ?? undefined,
  };
  sessionStore().setItem(USER_KEY, JSON.stringify(user));

  // Sentry : associe l'utilisateur courant pour enrichir les events futurs.
  // No-op si Sentry n'est pas initialisé (DSN absent en dev).
  sentrySetUser({
    id: data.userId || data.id || user.username,
    email: data.email || user.username,
    username: user.displayName || user.username,
    role: user.role,
    direction: user.direction || undefined,
  });

  return user;
}

export function logout(): void {
  clearTokens();
  sentryClearUser();
}

/**
 * Vrai si un access token est présent ET pas expiré. Utilisé par le route guard
 * et le queryClient pour décider d'une redirection /login.
 */
export function isAuthenticated(): boolean {
  if (typeof window === "undefined") return false;
  const token = readPersisted(TOKEN_KEY);
  if (!token) return false;
  const expiresAt = Number(readPersisted(EXPIRES_KEY) || "0");
  if (expiresAt && Date.now() > expiresAt) {
    clearTokens();
    return false;
  }
  return true;
}

interface AuthContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  hasSection: (sectionId: string) => boolean;
  /**
   * Chemin ouvert à l'utilisateur courant (liste blanche ROLE_PATHS, chemins
   * réservés aux administrateurs globaux, sinon libre).
   */
  hasPath: (path: string) => boolean;
  /** Profil en lecture seule : masquer / désactiver les boutons d'écriture. */
  isReadOnly: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  hasSection: () => true,
  hasPath: () => true,
  isReadOnly: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = readPersisted(USER_KEY);
    if (!raw) return null;
    try {
      return withRolesFromToken(JSON.parse(raw) as AuthUser, readPersisted(TOKEN_KEY));
    } catch {
      return null;
    }
  });

  const setUser = (next: AuthUser | null) => {
    setUserState(next);
    if (next) {
      sessionStore().setItem(USER_KEY, JSON.stringify(next));
      // Resync Sentry sur changement d'utilisateur (login direct via context).
      sentrySetUser({
        id: next.username,
        email: next.username,
        username: next.displayName,
        role: next.role,
        direction: next.direction || undefined,
      });
    } else {
      clearTokens();
      sentryClearUser();
    }
  };

  useEffect(() => {
    const expiresAt = Number(readPersisted(EXPIRES_KEY) || "0");
    if (expiresAt && Date.now() > expiresAt) {
      clearTokens();
      setUserState(null);
      sentryClearUser();
      return;
    }
    // Rehydration : si on a un user persisté en localStorage, on le repropage
    // à Sentry pour que les événements remontent avec l'identité dès le mount.
    if (user) {
      sentrySetUser({
        id: user.username,
        email: user.username,
        username: user.displayName,
        role: user.role,
        direction: user.direction || undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSection = (sectionId: string): boolean => {
    if (!user) return true;
    return ROLE_SECTIONS[user.role]?.includes(sectionId) ?? false;
  };

  const hasPath = (path: string): boolean => {
    if (!user) return true;
    return pathAllowedForUser(user, path);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, hasSection, hasPath, isReadOnly: !!user?.readOnly }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
